import React, { useEffect, useRef, useState, useMemo } from 'react';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import moment from 'moment';
import { ClipLoader } from 'react-spinners';

import filterIcon from "../../../../../../Assets/images/filter-icon.svg";
import clearFilterIcon from "../../../../../../Assets/images/clear-icon.svg";
import closeRedIcon from "../../../../../../Assets/images/close-red.svg";
import calendarIcon from "../../../../../../Assets/images/calender.svg";

import InvoiceViewModal from '../InvoiceViewModal/InvoiceViewModal';
import { DATE_FORMAT, DATE_MONTH_FORMAT } from '../../../Utils/Constants';
import Pagination from '../../../../../../Utils/Pagination/Pagination';

const HistoryInvoices = ({ invoices = [], loading, refreshInvoices }) => {
    const [isFilterOpen, setIsFilterOpen] = useState(false);
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [selectedInvoiceId, setSelectedInvoiceId] = useState(null);

    const dropdownRef = useRef(null);

    const [filters, setFilters] = useState({
        invoice_date_from: null,
        invoice_date_to: null,
        due_date_from: null,
        due_date_to: null,
        status: ''
    });

    const [appliedFilters, setAppliedFilters] = useState(filters);

    const [itemsPerPage, setItemsPerPage] = useState(10);
    const [currentPage, setCurrentPage] = useState(1);

    const toggleFilter = () => setIsFilterOpen(prev => !prev);

    useEffect(() => {
        const handleClickOutside = (e) => {
            if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
                setIsFilterOpen(false);
            }
        };
        document.addEventListener("mousedown", handleClickOutside);
        return () => document.removeEventListener("mousedown", handleClickOutside);
    }, []);

    const handleDateChange = (field, value) => {
        setFilters(prev => ({ ...prev, [field]: value }));
    };

    const handleStatusChange = (e) => {
        setFilters(prev => ({ ...prev, status: e.target.value }));
    };

    const clearDateRange = (fromField, toField) => {
        setFilters(prev => ({ ...prev, [fromField]: null, [toField]: null }));
    };

    const clearStatus = () => {
        setFilters(prev => ({ ...prev, status: '' }));
    };

    const clearAllFilters = () => {
        const cleared = {
            invoice_date_from: null,
            invoice_date_to: null,
            due_date_from: null,
            due_date_to: null,
            status: ''
        };
        setFilters(cleared);
        setAppliedFilters(cleared);
    };

    const applyFilters = () => {
        setAppliedFilters(filters);
        setCurrentPage(1);
        setIsFilterOpen(false);
    };

    const hasInvoiceDate = filters.invoice_date_from || filters.invoice_date_to;
    const hasDueDate = filters.due_date_from || filters.due_date_to;
    const hasStatus = !!filters.status;
    const isAnyFilterApplied = appliedFilters.invoice_date_from || appliedFilters.invoice_date_to || appliedFilters.due_date_from || appliedFilters.due_date_to || appliedFilters.status;

    const openInvoiceModal = (invoiceId) => {
        setSelectedInvoiceId(invoiceId);
        setIsModalOpen(true);
    };

    const filteredInvoices = useMemo(() => {
        return invoices.filter(inv => {
            const invDate = inv.inv_datetime ? moment(inv.inv_datetime) : null;
            const dueDate = inv.inv_due_date ? moment(inv.inv_due_date) : null;

            const matchInvoiceDate =
                (!appliedFilters.invoice_date_from || (invDate && invDate.isSameOrAfter(appliedFilters.invoice_date_from, 'day'))) &&
                (!appliedFilters.invoice_date_to || (invDate && invDate.isSameOrBefore(appliedFilters.invoice_date_to, 'day')));

            const matchDueDate =
                (!appliedFilters.due_date_from || (dueDate && dueDate.isSameOrAfter(appliedFilters.due_date_from, 'day'))) &&
                (!appliedFilters.due_date_to || (dueDate && dueDate.isSameOrBefore(appliedFilters.due_date_to, 'day')));

            const matchStatus =
                appliedFilters.status === '' ||
                (appliedFilters.status === 'Paid' && inv.paid_status === 1) ||
                (appliedFilters.status === 'Unpaid' && inv.paid_status === 0);

            return matchInvoiceDate && matchDueDate && matchStatus;
        });
    }, [invoices, appliedFilters]);

    const totalInvoices = filteredInvoices.length;
    const totalPages = Math.ceil(totalInvoices / itemsPerPage);
    const startIndex = (currentPage - 1) * itemsPerPage;

    const currentInvoices = useMemo(() => {
        return filteredInvoices.slice(startIndex, startIndex + itemsPerPage);
    }, [filteredInvoices, startIndex, itemsPerPage]);

    const handlePageChange = (page) => {
        if (page >= 1 && page <= totalPages) {
            setCurrentPage(page);
        }
    };

    return (
        <>
            <div className="common-top exm-top">
                <div className="filter-cvr dropdown" ref={dropdownRef}>
                    <a className={`filter-btn ${isAnyFilterApplied ? "active-filter" : ""}`} href="#" onClick={(e) => { e.preventDefault(); toggleFilter(); }}>
                        <span className="filter-icon-wrapper">
                            <img className="ct-icon" src={filterIcon} alt="Filter" />
                            {isAnyFilterApplied && <span className="filter-indicator-dot"></span>}
                        </span>
                        Filter
                    </a>

                    <a className="clear-btn" href="#" onClick={(e) => { e.preventDefault(); clearAllFilters(); }}>
                        <img className="ct-icon" src={clearFilterIcon} alt="Clear" />
                        Clear Filter
                    </a>

                    {isFilterOpen && (
                        <ul className="dropdown-menu filterdrop-body show">
                            <div className="filterdrop-split">
                                <label className="filter-label">
                                    Invoice Date
                                    {hasInvoiceDate && (
                                        <img className="red-close" src={closeRedIcon} alt="remove" style={{ cursor: "pointer" }} onClick={() => clearDateRange("invoice_date_from", "invoice_date_to")} />
                                    )}
                                </label>
                                <div className="filter-datepicker from-date">
                                    <DatePicker
                                        selected={filters.invoice_date_from}
                                        onChange={(date) => handleDateChange("invoice_date_from", date)}
                                        placeholderText="Start Date"
                                        className="filter-date"
                                        dateFormat={DATE_MONTH_FORMAT}
                                        showMonthDropdown
                                        showYearDropdown
                                        dropdownMode="select"
                                        popperProps={{ strategy: 'fixed' }}
                                    />
                                    <span className="filter-calendar-icon"><img src={calendarIcon} alt="calendar" /></span>
                                </div>
                                <div className="filter-datepicker to-date">
                                    <DatePicker
                                        selected={filters.invoice_date_to}
                                        onChange={(date) => handleDateChange("invoice_date_to", date)}
                                        placeholderText="End Date"
                                        className="filter-date"
                                        dateFormat={DATE_MONTH_FORMAT}
                                        showMonthDropdown
                                        showYearDropdown
                                        dropdownMode="select"
                                        popperProps={{ strategy: 'fixed' }}
                                    />
                                    <span className="filter-calendar-icon"><img src={calendarIcon} alt="calendar" /></span>
                                </div>
                            </div>

                            <div className="filterdrop-split">
                                <label className="filter-label">
                                    Invoice Due Date
                                    {hasDueDate && (
                                        <img className="red-close" src={closeRedIcon} alt="remove" style={{ cursor: "pointer" }} onClick={() => clearDateRange("due_date_from", "due_date_to")} />
                                    )}
                                </label>
                                <div className="filter-datepicker from-date">
                                    <DatePicker
                                        selected={filters.due_date_from}
                                        onChange={(date) => handleDateChange("due_date_from", date)}
                                        placeholderText="Start Date"
                                        className="filter-date"
                                        dateFormat={DATE_MONTH_FORMAT}
                                        showMonthDropdown
                                        showYearDropdown
                                        dropdownMode="select"
                                        popperProps={{ strategy: 'fixed' }}
                                    />
                                    <span className="filter-calendar-icon"><img src={calendarIcon} alt="calendar" /></span>
                                </div>
                                <div className="filter-datepicker to-date">
                                    <DatePicker
                                        selected={filters.due_date_to}
                                        onChange={(date) => handleDateChange("due_date_to", date)}
                                        placeholderText="End Date"
                                        className="filter-date"
                                        dateFormat={DATE_MONTH_FORMAT}
                                        showMonthDropdown
                                        showYearDropdown
                                        dropdownMode="select"
                                        popperProps={{ strategy: 'fixed' }}
                                    />
                                    <span className="filter-calendar-icon"><img src={calendarIcon} alt="calendar" /></span>
                                </div>
                            </div>

                            <div className="filterdrop-split">
                                <label className="filter-label">
                                    Invoice Status
                                    {hasStatus && (
                                        <img className="red-close" src={closeRedIcon} alt="remove" style={{ cursor: "pointer" }} onClick={clearStatus} />
                                    )}
                                </label>
                                <select className="filter-select" value={filters.status} onChange={handleStatusChange}>
                                    <option value="">--select--</option>
                                    <option value="Paid">Paid</option>
                                    <option value="Unpaid">Unpaid</option>
                                </select>
                            </div>

                            <div className="filterdrop-split mb-0">
                                <div className="filter-buttons">
                                    <a href="#" className="filter-clear" onClick={(e) => { e.preventDefault(); clearAllFilters(); }}>Clear</a>
                                    <a href="#" className="filter-apply" onClick={(e) => { e.preventDefault(); applyFilters(); }}>Apply</a>
                                </div>
                            </div>
                        </ul>
                    )}
                </div>
            </div>

            <div className="table-cvr">
                <div className="table-inner">
                    <table className="table">
                        <thead>
                            <tr>
                                <th className="text-middle">#</th>
                                <th className="invoice-col3">Invoice Id</th>
                                <th className="invoice-col4">Invoice Date</th>
                                <th className="invoice-col5">Date Range</th>
                                <th className="invoice-col6">No of Jobs</th>
                                <th className="invoice-col7" style={{width: '125px' }}>Invoice Amount</th>
                                <th className="invoice-col8">Due Date</th>
                                <th>Status</th>
                                <th>Paid Date</th>
                            </tr>
                        </thead>
                        <tbody>
                            {loading ? (
                                <tr>
                                    <td colSpan="9" className="text-center py-4">
                                        <ClipLoader size={30} color="gray" />
                                    </td>
                                </tr>
                            ) : currentInvoices.length > 0 ? (
                                currentInvoices.map((inv, index) => (
                                    <tr key={inv.id}>
                                        <td className="text-middle">{startIndex + index + 1}</td>
                                        <td>
                                            <a className="table-link" href="#" onClick={(e) => { e.preventDefault(); openInvoiceModal(inv.id); }}>
                                                {inv.inv_no || '--'}
                                            </a>
                                        </td>
                                        <td>{inv.inv_datetime ? moment.utc(inv.inv_datetime).local().format(DATE_FORMAT) : '--'}</td>
                                        <td>{inv.inv_start_date_only ? moment(inv.inv_start_date_only, "YYYY-MM-DD").format(DATE_FORMAT) : '--'} - {inv.inv_end_date_only ? moment(inv.inv_end_date_only, "YYYY-MM-DD").format(DATE_FORMAT) : '--'}</td>
                                        <td className="invoice-col6">{inv.jobs_count ?? '--'}</td>
                                        <td className='align-right'>${Number((inv.inv_amount || 0) - ((inv.inv_amount || 0) * inv.commission_rate)).toFixed(2)}</td>
                                        <td>{inv.inv_due_date ? moment.utc(inv.inv_due_date).local().format(DATE_FORMAT) : '--'}</td>
                                        <td>
                                            {inv.paid_status === 1 ? (
                                                <p className="paid-status">Paid</p>
                                            ) : (
                                                <p className="pending-status">Pending</p>
                                            )}
                                        </td>
                                        <td>{inv.paid_datetime ? moment.utc(inv.paid_datetime).local().format(DATE_FORMAT) : '--'}</td>
                                    </tr>
                                ))
                            ) : (
                                <tr>
                                    <td colSpan="9" className="text-center py-4 text-muted">
                                        No invoices found.
                                    </td>
                                </tr>
                            )}
                        </tbody>
                    </table>
                </div>

                <Pagination
                    totalItems={totalInvoices}
                    itemsPerPage={itemsPerPage}
                    currentPage={currentPage}
                    onPageChange={setCurrentPage}
                    setItemsPerPage={setItemsPerPage}
                    totalPages={totalPages}
                />
            </div>

            <InvoiceViewModal show={isModalOpen} invoiceId={selectedInvoiceId} onClose={() => setIsModalOpen(false)} refreshInvoices={refreshInvoices} />
        </>
    );
};

export default HistoryInvoices;